import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoredFundDeckToken, submitFundDeckRequest, validateStoredDeckSession } from '../../lib/submitFundDeckRequest';
import { FUND_DECK_PHONE_ERROR, isValidFundDeckPhone } from '../../lib/fundDeckPhone';
import { userMessageFromCallableError } from '../../lib/firebaseCallableErrors';
import '../../styles/kyro-design.css';

const sectors = [
  { badge: 'b-core', label: 'Core Focus', name: 'Manufacturing & Deep-Tech', theme: 'PLI-linked tailwinds, import substitution and precision engineering — export-ready units.' },
  { badge: 'b-core', label: 'Core Focus', name: 'Power & Renewable Energy', theme: 'Solar, wind, green hydrogen and smart grid players riding ₹11L Cr govt capex in FY26.' },
  { badge: 'b-alpha', label: 'High Alpha', name: 'FMCG, Packaged Food & Exports', theme: 'Organised brands displacing unorganised players and export-oriented specialty food units.' },
  { badge: 'b-alpha', label: 'High Alpha', name: 'AI & Product-Led Technology', theme: 'B2B SaaS and IT product companies — AI-native architecture and defensible IP.' },
  { badge: 'b-steady', label: 'Steady Alpha', name: 'Infrastructure & Logistics', theme: 'Cold chain, warehousing, last-mile logistics and data centre infrastructure.' },
  { badge: 'b-opp', label: 'Opportunistic', name: 'Financial Services & Capital Markets', theme: "Wealth management, broking, NBFCs and specialty lenders riding India's financialisation wave." },
];

const processSteps = [
  { n: '01', title: 'Deal Sourcing', desc: 'Proprietary pipeline via investment bankers, co-investors and capital market networks.', edge: 'Direct founder outreach — we approach targets before formal fundraising begins.' },
  { n: '02', title: 'Preliminary Screening', desc: 'Business model quality, sector tailwinds, financial health and promoter track record.', edge: 'Sector deep-dive calls with industry experts before any capital commitment.' },
  { n: '03', title: 'Deep Due Diligence', desc: 'Financial modelling, legal review, management meetings and compliance verification.', edge: 'Mandatory on-site factory visit by Fund Manager — every time, no exceptions.' },
  { n: '04', title: 'IC Approval', desc: 'Investment Committee sign-off on risk/return, valuation and governance terms.', edge: 'IC memo includes a written Bear Case — downside stress-tested as rigorously as upside.' },
  { n: '05', title: 'Portfolio Monitoring', desc: 'Active KPI tracking, board engagement and strategic value creation support.', edge: 'Monthly management call + quarterly site visit. No passive holding.' },
  { n: '06', title: 'Exit Execution', desc: 'Tailored exits via IPO listing, block deals, strategic M&A or structured buyouts.', edge: 'Exit preparation begins 12 months in advance — positioning and timing discipline.' },
];

const fundCells = [
  { l: 'Fund Name', v: 'Kyro India Opportunities Fund – I (scheme of Kyro Alternative Investment Trust – Category-II AIF)' },
  { l: 'Legal Structure', v: 'Category II AIF – Closed-Ended (Trust)' },
  { l: 'SEBI Registration No.', v: 'IN/AIF2/26-27/2191' },
  { l: 'Trustee', v: 'Orbis Trusteeship Services Private Limited' },
  { l: 'Sponsor', v: 'Kyro Capital Private Limited' },
  { l: 'Investment Manager', v: 'Kyro Asset Management Private Limited' },
  { l: 'Fund Tenure', v: '5 Years (+2 Year Extensions)' },
  { l: 'Minimum Commitment', v: '₹1 Crore per Investor', sub: 'USD 119,000 · EUR 110,000 · GBP 94,000 | Called in 4 equal tranches over 18 months', subEm: 'Fee structure available in the fund deck' },
  { l: 'Sponsor Commitment', v: '2.5% of Corpus or ₹5 Crore (whichever is lower)', sub: 'Aligned from Day 1 — skin in the game' },
];

const pillars = [
  { n: '01', t: 'Growth-Oriented Selection', d: 'Companies with demonstrated operating leverage and a clear path to scale or listing.' },
  { n: '02', t: 'Pre-IPO & Listed Spectrum', d: 'From pre-IPO entry through IPO, PIPE, secondary and structured exit — value at every stage.' },
  { n: '03', t: 'Rigorous Due Diligence', d: 'Mandatory on-site visits, financial modelling, legal review and IC approval with a written Bear Case.' },
  { n: '04', t: 'Active Portfolio Management', d: 'Monthly management calls, quarterly site visits. No passive holding — no exceptions.' },
];

const AssetManagement: React.FC = () => {
  const navigate = useNavigate();
  const [deckChecking, setDeckChecking] = useState(true);
  const [deckSessionActive, setDeckSessionActive] = useState(false);
  const [deckError, setDeckError] = useState('');
  const [deckLoading, setDeckLoading] = useState(false);
  const [deckFields, setDeckFields] = useState({ name: '', org: '', email: '', phone: '' });

  useEffect(() => {
    let c = false;
    (async () => {
      setDeckChecking(true);
      const ok = await validateStoredDeckSession();
      if (!c) {
        setDeckSessionActive(ok);
        setDeckChecking(false);
      }
    })();
    return () => {
      c = true;
    };
  }, []);

  const submitDeck = async () => {
    setDeckError('');
    const name = deckFields.name.trim();
    const phone = deckFields.phone.trim();
    const email = deckFields.email.trim();
    if (!name || !phone || !email) {
      setDeckError('Name, email and phone number are required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setDeckError('Please enter a valid email address.');
      return;
    }
    if (!isValidFundDeckPhone(phone)) {
      setDeckError(FUND_DECK_PHONE_ERROR);
      return;
    }
    setDeckLoading(true);
    try {
      const token = await submitFundDeckRequest({
        name,
        phone,
        email,
        organisation: deckFields.org.trim() || null,
      });
      setDeckSessionActive(true);
      setDeckFields({ name: '', org: '', email: '', phone: '' });
      navigate(`/fund-deck?t=${encodeURIComponent(token)}`);
    } catch (err) {
      console.error(err);
      setDeckError(userMessageFromCallableError(err));
    } finally {
      setDeckLoading(false);
    }
  };

  const goToFundDeck = () => {
    const t = getStoredFundDeckToken();
    if (!t) {
      setDeckSessionActive(false);
      return;
    }
    navigate(`/fund-deck?t=${encodeURIComponent(t)}`);
  };

  return (
    <div className="page">
      <div className="hero" style={{ minHeight: '50vh', paddingBottom: '4rem' }}>
        <div className="hero-grid" />
        <div className="hero-glow" />
        <div className="hero-inner">
          <div className="kicker">
            <div className="kicker-dot" />
            <div className="kicker-line" />
            Asset Management
          </div>
          <h1 style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)' }}>
            We See Value<br />
            <span className="dim">Before It Becomes Obvious.</span>
          </h1>
          <p className="hero-sub">
            A SEBI Registered Category II Alternative Investment Fund targeting India's most compelling growth-stage and listed opportunities. Built for investors who think in decades, not quarters.
          </p>
        </div>
      </div>

      <div className="stats">
        {[
          { v: '35%', l: 'Target IRR' },
          { v: '₹1 Cr', l: 'Min. Investment' },
          { v: '5 Yrs', l: 'Tenure (+2 Ext.)' },
          { v: '₹100 Cr', l: 'Target Corpus' },
        ].map((s) => (
          <div className="stat" key={s.l}>
            <div className="stat-v">{s.v}</div>
            <div className="stat-l">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="sec-off">
        <div className="grid-2">
          <div>
            <div className="eyebrow">Investment Philosophy</div>
            <div className="sec-title">
              Disciplined Capital.<br />Asymmetric Returns.
            </div>
            <p className="sec-body" style={{ marginBottom: '1rem' }}>
              Our approach is grounded in bottom-up fundamental research, valuation discipline and active portfolio governance — applied consistently across every investment we make.
            </p>
            <p className="sec-body">
              We target the highest-alpha segment of Indian capital markets — pre-IPO entry and listed PIPE opportunities — across Manufacturing, Power, FMCG, AI and Infrastructure sectors.
            </p>
          </div>
          <div className="pillars">
            {pillars.map((p) => (
              <div className="pillar" key={p.n}>
                <div className="pillar-num">{p.n}</div>
                <div>
                  <div className="pillar-title">{p.t}</div>
                  <div className="pillar-desc">{p.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="sec">
        <div className="eyebrow">Fund at a Glance</div>
        <div className="sec-title">Principal Terms.</div>
        <div className="fund-grid">
          {fundCells.map((c) => (
            <div className="fund-cell" key={c.l}>
              <div className="cell-label">{c.l}</div>
              <div className="cell-val">{c.v}</div>
              {c.sub && (
                <div className="cell-sub">
                  {c.sub.split('|').map((s, i) => (
                    <div key={i}>{s.trim()}</div>
                  ))}
                  {c.subEm && <em>{c.subEm}</em>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="sec-off">
        <div className="eyebrow">Sector Focus</div>
        <div className="sec-title">Where We Invest.</div>
        <div className="sec-grid">
          {sectors.map((s) => (
            <div className="sec-item" key={s.name}>
              <span className={`sec-badge ${s.badge}`}>{s.label}</span>
              <div className="sec-name">{s.name}</div>
              <div className="sec-theme">{s.theme}</div>
            </div>
          ))}
        </div>
        <div className="excl">
          <div className="excl-lbl">Exclusion List</div>
          {['Alcohol & Distilleries', 'Non-Vegetarian / Poultry / Seafood', 'Gambling & Fantasy Sports', 'Tobacco & Related Products'].map((e) => (
            <span className="excl-tag" key={e}>{e}</span>
          ))}
        </div>
        <p style={{ fontSize: '0.67rem', color: 'var(--gray)', marginTop: '0.7rem', fontStyle: 'italic' }}>
          Sector exclusions reflect the Investment Manager's founding principles and apply permanently across all fund vehicles.
        </p>
      </div>

      <div className="sec">
        <div className="edge-note">
          <strong style={{ color: 'var(--blue)' }}>★ What Sets Us Apart —</strong> Fund Manager personally leads every site visit, management meeting and post-investment review. Not delegated. Not outsourced.
        </div>
        <div className="eyebrow">Investment Process</div>
        <div className="sec-title">Institutional Discipline. Founder-Level Commitment.</div>
        <div className="proc-grid">
          {processSteps.map((s) => (
            <div className="proc-card" key={s.n}>
              <div className="proc-head">
                <div className="proc-num">{s.n}</div>
                <div className="proc-title">{s.title}</div>
              </div>
              <div className="proc-body">
                <div className="proc-desc">{s.desc}</div>
                <span className="proc-edge-lbl">Our Edge</span>
                <div className="proc-edge">{s.edge}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="deck-sec" id="deck-req">
        <div className="deck-glow" />
        <div className="deck-top-line" />
        <div className="deck-inner">
          <div className="eyebrow eyebrow-lt">Fund deck</div>
          <h2 className="sora" style={{ fontSize: 'clamp(1.7rem, 3vw, 2.4rem)', fontWeight: 600, color: '#fff', marginBottom: '0.7rem', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            This isn't just a fund.<br />
            <span style={{ color: 'rgba(255,255,255,0.35)', fontWeight: 300 }}>It's a partnership.</span>
          </h2>
          <p style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.42)', lineHeight: 1.8 }}>
            Fund terms, fee structure, strategy and track record — for eligible investors.
          </p>
          {deckChecking ? (
            <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.35)', marginTop: '1.5rem' }}>…</p>
          ) : deckSessionActive ? (
            <div style={{ marginTop: '1.75rem' }}>
              <button type="button" className="d-submit" onClick={goToFundDeck} style={{ width: '100%', maxWidth: 320 }}>
                View fund deck →
              </button>
            </div>
          ) : (
            <>
              <div className="deck-form">
                <input className="d-input" placeholder="Your Name *" value={deckFields.name} onChange={(e) => setDeckFields({ ...deckFields, name: e.target.value })} required />
                <input className="d-input" type="email" placeholder="Email Address *" value={deckFields.email} onChange={(e) => setDeckFields({ ...deckFields, email: e.target.value })} required />
                <input className="d-input" type="tel" placeholder="Phone Number *" value={deckFields.phone} onChange={(e) => setDeckFields({ ...deckFields, phone: e.target.value })} required />
                <input
                  className="d-input"
                  placeholder="Referred By (Distributor Name) / Organization"
                  aria-label="Referred By (Distributor Name) / Organization (optional)"
                  value={deckFields.org}
                  onChange={(e) => setDeckFields({ ...deckFields, org: e.target.value })}
                />
                {deckError && <div style={{ fontSize: '0.8rem', color: '#FCA5A5' }}>{deckError}</div>}
                <button className="d-submit" onClick={submitDeck} disabled={deckLoading}>
                  {deckLoading ? 'Opening deck…' : 'View fund deck →'}
                </button>
              </div>
              <div className="or-div">or reach us directly</div>
              <div className="direct">
                <a href="tel:+917611111102">✆ +91 76 111 111 02</a>
                <a href="mailto:compliance@kyrocapital.in">✉ compliance@kyrocapital.in</a>
              </div>
            </>
          )}
          <div className="disc">
            Kyro India Opportunities Fund – I is a SEBI-registered Category II AIF (Reg. No. IN/AIF2/26-27/2191). This page is for informational purposes only and does not constitute an offer to sell or subscribe to any securities. For eligible investors under SEBI (AIF) Regulations, 2012. Target returns are indicative only. For Private Circulation Only.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetManagement;
